import * as IoC from 'typescript-ioc';
import { IApiClient, RequestContentType } from '../../infrastructure/api-client';
import {
    IAssetUrlDTO,
    IConfigurationParams,
    ICreateEventReportDTO,
    ICreateReportDTO,
    IGenerateEventReportParams,
    IGetReportsRequestParams,
    IReportConfigItem,
    IReportConfiguration,
    IReportDetailsDTO,
    IReportFileDownloadUrlDTO,
    IReportListItemDTO,
    IReportsDAO,
    ISaveConfigurationParams,
    IStartDateTimesDTO,
    IUpdateReportDTO,
    ReportLayout
} from '../reports.dao';
import { IEventRequestParams, replaceSortRequestParamsWithSortString } from '../shared.dto';
import * as REPORTS_API from '../../constants/api/reports-api.constants';
import { JobDtoType } from '../../domain/jobs';
import { RetypePartial } from '../../infrastructure/type-extensions';
import { IApiDateConverter } from '../../infrastructure/api-date-converter';
import { getFileTypeFromName } from '../../infrastructure/utils/file.utils';
import { APPLICATION_OCTET_STREAM } from '../../constants/file.constants';

type IReportListItemDTOInternal = RetypePartial<IReportListItemDTO, 'uploadDateTime', string>;

class ReportsDAO extends IReportsDAO {
    constructor(
        @IoC.Inject private readonly apiClient: IApiClient,
        @IoC.Inject private readonly apiDateConverter: IApiDateConverter) {
        super();
    }

    public async getReports(params: IGetReportsRequestParams): Promise<IReportListItemDTO[]> {
        const paramsWithSortString = replaceSortRequestParamsWithSortString(params);
        const response = await this.apiClient.get<IReportListItemDTOInternal[]>(REPORTS_API.REPORTS_LIST_URL, paramsWithSortString);
        return response.parsedResponse.map(x => this.mapInternalReportDTOToPublicDTO(x));
    }

    public async deleteReport(params: IGetReportsRequestParams & { assetMetadataId: number }): Promise<void> {
        await this.apiClient.delete<void>(REPORTS_API.DELETE_REPORT_URL, params);
    }

    public async updateReport(params: IGetReportsRequestParams & IUpdateReportDTO & { assetMetadataId: number }): Promise<IReportDetailsDTO> {
        const response = await this.apiClient.put<IReportDetailsDTO>(REPORTS_API.UPDATE_REPORT_URL, params);
        return response.parsedResponse;
    }

    public async createReport(params: ICreateReportDTO & IEventRequestParams): Promise<void> {
        const { reportFile, ...rest } = params;
        const assetUrlParams = await this.getS3AssetUrl({
            accountId: rest.accountId,
            eventId: rest.eventId
        });
        await this.uploadReportToS3(assetUrlParams.assetUrl, reportFile);
        await this.apiClient.post(
            REPORTS_API.CREATE_REPORT_URL, {
            ...rest,
            objectKey: assetUrlParams.objectKey
        }
        );
    }

    private async getS3AssetUrl(params: IEventRequestParams): Promise<IAssetUrlDTO> {
        const response = await this.apiClient.get<IAssetUrlDTO>(REPORTS_API.GET_REPORT_S3_URL, params);
        return response.parsedResponse;
    }

    private async uploadReportToS3(url: string, reportFile: File) {
        let fileType = reportFile.type;
        if (!fileType || fileType === APPLICATION_OCTET_STREAM) {
            fileType = RequestContentType[getFileTypeFromName(reportFile)];
        }

        const response = await this.apiClient.put<JobDtoType>(url, reportFile, {
            ContentType: fileType as RequestContentType
        });
        return response.parsedResponse;
    }

    public async getReportFileDownloadUrl(params: IEventRequestParams & { assetMetadataId: number }): Promise<IReportFileDownloadUrlDTO> {
        const response = await this.apiClient.get<IReportFileDownloadUrlDTO>(REPORTS_API.REPORT_FILE_DOWNLOAD_URL, params);
        return response.parsedResponse;
    }

    public async getReportFile(url: string, fileName: string) {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.download = fileName;
        a.href = blobUrl;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(blobUrl);
    }

    public generateEventInteractionsReport = async (params: IEventRequestParams & ICreateEventReportDTO): Promise<IReportListItemDTO> => {
        const response = await this.apiClient.post<IReportListItemDTO>(REPORTS_API.GENERATE_EVENT_INTERACTION_REPORT_URL, params);
        return response.parsedResponse;
    };

    public generateAttendeeInteractionsReport = async (
        params: IGenerateEventReportParams,
        meetingIds: number[],
        layoutType: ReportLayout
    ): Promise<IReportListItemDTO> => {
        const response = await this.apiClient.post<IReportListItemDTO>(REPORTS_API.GENERATE_ATTENDEE_INTERACTION_REPORT_URL, {
            ...params,
            meetingIds: meetingIds,
            layoutType: layoutType,
        });
        return response.parsedResponse;
    };

    public generateRegistrantsBySessionReport = async (params: IGenerateEventReportParams): Promise<IReportListItemDTO> => {
        const response = await this.apiClient.post<IReportListItemDTO>(REPORTS_API.GENERATE_REGISTRANTS_BY_SESSION_REPORT_URL, params);
        return response.parsedResponse;
    };

    public generateMeetingInteractionsReport = async (params: IGenerateEventReportParams, meetingIds: number[]): Promise<IReportListItemDTO> => {
        const response = await this.apiClient.post<IReportListItemDTO>(REPORTS_API.GENERATE_MEETING_INTERACTIONS_REPORT_URL, {
            ...params,
            meetingIds: meetingIds
        }, );
        return response.parsedResponse;
    };

    public generateMeetingRegistrationReport = async (params: IGenerateEventReportParams, meetingIds: number[]): Promise<IReportListItemDTO> => {
        const response = await this.apiClient.post<IReportListItemDTO>(REPORTS_API.GENERATE_MEETING_REGISTRATION_REPORT_URL, {
            ...params,
            meetingIds: meetingIds
        }, );
        return response.parsedResponse;
    };

    public generateEventRegistrationReport = async (params: IEventRequestParams): Promise<IReportListItemDTO> => {
        const response = await this.apiClient.post<IReportListItemDTO>(REPORTS_API.GENERATE_EVENT_REGISTRATION_REPORT_URL, params);
        return response.parsedResponse;
    };

    public async getDefaultEventReportConfiguration(params: IConfigurationParams): Promise<IReportConfigItem[]> {
        const response = await this.apiClient.get<IReportConfigItem[]>(REPORTS_API.EVENT_REPORT_DEFAULT_CONFIG_URL, params);
        return response.parsedResponse;
    }

    private mapInternalReportDTOToPublicDTO(internalDTO: IReportListItemDTOInternal) {
        return {
            ...internalDTO,
            uploadDateTime: this.apiDateConverter.apiStringToDate(`${internalDTO.uploadDateTime}Z`),
        };
    }

    public async getMeetingsReportStartDateTimes(params: IEventRequestParams): Promise<IStartDateTimesDTO> {
        const response = await this.apiClient.get<IStartDateTimesDTO>(REPORTS_API.MEETING_REPORT_START_DATE_TIMES_URL, params);
        return response.parsedResponse;
    }

    public async getConfigurations(params: IConfigurationParams): Promise<IReportConfiguration[]> {
        const response = await this.apiClient.get<IReportConfiguration[]>(REPORTS_API.CONFIGURATIONS_URL, params);
        return response.parsedResponse;
    }

    public async saveConfiguration(params: ISaveConfigurationParams): Promise<IReportConfiguration> {
        const response = await this.apiClient.post<IReportConfiguration>(REPORTS_API.CONFIGURATIONS_URL, params);
        return response.parsedResponse;
    }

    public async deleteConfiguration(params: IEventRequestParams & { reportConfigurationTemplateId: number}): Promise<IReportConfiguration> {
        const response = await this.apiClient.delete<IReportConfiguration>(REPORTS_API.DELETE_CONFIGURATION_URL, params);
        return response.parsedResponse;
    }
}

export { ReportsDAO };
